/**
 * The Loading vs Capacity ENCODING — how a week becomes marks on screen.
 *
 * Kept in one module because the encoding is the product. The same four segments have to
 * mean the same thing in the small multiple, in the full-size chart and in the clearance
 * lane, or the surface teaches three different readings of one number. The handoff (§
 * Encoding) is the specification; this is that specification, once.
 *
 * Deliberately NOT in components/kit/cellFormat.ts: that seam takes a registry-served Cell
 * so a naked number cannot reach the scorecard grid without its trust envelope (CD-7).
 * These are plain hours off a mart, and mixing the two would weaken the guarantee.
 */

import type { CSSProperties } from 'react'
import type { WeekModel } from '../../viewmodels/loading'

export const hrs = (v: number): string =>
  `${Math.round(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}h`

/**
 * Hours at OPERATION grain, where whole hours are the wrong unit. A works order's route is
 * full of ten-minute inspections, and `hrs` renders every one of them as "0h" — which reads
 * as "no time booked" when it means "a sixth of an hour". Weekly and group totals keep the
 * coarse form; a single operation gets a decimal until it is big enough not to need one.
 */
export const hrsFine = (v: number): string => `${v < 10 ? v.toFixed(1) : Math.round(v)}h`

const DATE = (iso: string): Date => new Date(`${iso}T00:00:00`)

/** '03 Aug' — the axis label. */
export const weekLabel = (iso: string): string =>
  DATE(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })

/** '03' — the compact axis label the small multiples use. */
export const weekShort = (iso: string): string => weekLabel(iso).slice(0, 2)

/**
 * Label every Nth week on a weekly axis, so a wide horizon does not smear.
 *
 * The horizon is a choice since 31 Aug 2026, and the columns keep the width the container
 * gives them: 52 weeks in the space of 13 is about 10px a column, where every label printed
 * is a grey wash that says less than no labels at all. The gap keeps roughly 13 ticks on any
 * axis, which is exactly what the fixed 13-week window always drew.
 */
export const tickEvery = (count: number): number => Math.max(1, Math.ceil(count / 13))

/** 'AUG' — the month a week starts in, for the lane ruler's month row. */
export const weekMonth = (iso: string): string =>
  DATE(iso).toLocaleDateString('en-GB', { month: 'short' }).toUpperCase()

/** 'w/c 03 Aug 26' — the week said out loud, in tooltips and the week panel. */
export const weekFull = (iso: string): string =>
  `w/c ${DATE(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}`

/** One stacked segment. The bar renders column-reverse, so index 0 sits on the floor. */
export interface Seg {
  key: string
  className?: string
  style: CSSProperties
}

const seg = (share: number, background: string, extra?: CSSProperties): CSSProperties => ({
  height: `${Math.max(0, share) * 100}%`,
  background,
  flex: 'none',
  ...extra,
})

/** The still-queued ghost is capped so a huge backlog cannot blow the axis — it is a
 *  presence, not a magnitude, and the tooltip carries the number. */
const GHOST_CAP = 0.3

/**
 * Levelled: clearing → due this week → free, stacking to EXACTLY capacity, with the
 * still-queued ghost drawn above the capacity line. The bar is therefore always full, and
 * the information is in the mix rather than the height — the copy has to say so, because a
 * full bar here is not "100% utilised", it is "there is more work than hours".
 *
 * `ghost` is off on the full-size chart: there the same fact is drawn as the burn-down
 * LINE, and carrying both would say it twice and hide the bars behind thirteen dashed boxes.
 */
export function levelledSegs(w: WeekModel, scale: number, ghost = true): Seg[] {
  const out: Seg[] = []
  if (w.clearing > 0) out.push({ key: 'clearing', style: seg(w.clearing / scale, ageRed(w.clearingAge)) })
  if (w.own > 0) out.push({ key: 'own', style: seg(w.own / scale, 'var(--lc-teal)') })
  if (w.free > 0) out.push({ key: 'free', style: seg(w.free / scale, 'var(--lc-free)') })
  if (ghost && w.carry > 0) {
    out.push({
      key: 'queued',
      style: seg(Math.min(w.carry, w.capacity * GHOST_CAP) / scale, 'var(--lc-ghost-fill)', {
        border: '1px dashed var(--lc-ghost-line)',
        borderBottom: 'none',
        boxSizing: 'border-box',
        marginBottom: '1px',
      }),
    })
  }
  return out
}

/** The load band an as-scheduled week falls in. Colour is by ratio here, by composition
 *  in the levelled view — which is why the legend has to swap with the toggle. */
export function band(scheduled: number, capacity: number): string {
  if (!capacity) return 'var(--lc-teal)'
  const ratio = scheduled / capacity
  if (ratio > 1) return 'var(--lc-red)'
  return ratio > 0.85 ? 'var(--lc-amber)' : 'var(--lc-teal)'
}

/**
 * As scheduled: one banded column per week, with week 1 carrying the folded pre-window
 * backlog as a hatched block underneath it. That column reads high BY DESIGN and the
 * hatch is what says so — without it, years of arrears landing in one bucket reads as
 * "next week is on fire".
 */
export function scheduledSegs(w: WeekModel, overdue: number, scale: number): Seg[] {
  const out: Seg[] = []
  const late = w.index === 0 ? Math.min(overdue, scale) : 0
  if (late > 0) out.push({ key: 'overdue', className: 'lc-hatch', style: seg(late / scale, '') })
  const rest = Math.max(0, Math.min(w.scheduled, scale) - (w.index === 0 ? overdue : 0))
  out.push({ key: 'sched', style: seg(rest / scale, band(w.scheduled, w.capacity)) })
  return out
}

/** A clearance-lane cell: still behind (hatch) · comfortable (soft teal) · full but
 *  current (teal). Three states, because "at capacity" and "behind" are not the same
 *  answer to "can we take this on". */
/** The red a lane cell or a clearing block is drawn in, at the depth its age earns. */
export function ageRed(weeks: number | null): string {
  const depth = ageDepth(weeks)
  const alpha = depth === null ? 0.22 : 0.22 + 0.68 * depth
  return `color-mix(in srgb, var(--lc-hatch-a) ${Math.round(alpha * 100)}%, transparent)`
}

/** Weeks late → 0..1 on a LOG scale (20 Aug 2026, Mike's 3b).
 *
 *  Linear would be useless here: even the corrected book spans 1 to 36 weeks and two thirds
 *  of it sits under 4, so on a linear ramp almost everything would be the palest red. Log
 *  spreads what is actually there — a doubling of lateness is one step.
 *
 *  ⚠ THE CEILING WAS 208 WEEKS — FOUR YEARS — AND IT WAS WRONG (corrected 20 Aug 2026).
 *  Its comment read "four years, which the book really does reach", and the book did reach it:
 *  on TWO works orders carrying 84.1h between them, 0.26% of the served hours (gap G38). Once
 *  completed operations stopped being counted as forward load (G37) and the lane stopped
 *  leading with the oldest, the real span of `clearing_age_weeks` measured 1.0 to 36.0 weeks
 *  across both sites, with 64% of cells under 4 weeks. Against a 208-week ceiling the deepest
 *  cell in the whole book reached 67% of the ramp and the top third was unreachable — so
 *  every lane rendered in the same middle-pale band and the encoding said nothing.
 *
 *  ONE YEAR is the ceiling now: round, governed, comfortably past today's worst case, and
 *  clamping beyond it is a real signal rather than an artefact — more than a year late SHOULD
 *  be the deepest red there is. It is deliberately FIXED and not derived from the data in
 *  view, so the same 20-week cell is the same red at Group, at Kirkstall and in a screenshot
 *  taken next month. A ramp that re-scales itself cannot be compared with itself. */
const AGE_FLOOR = 1
const AGE_CEILING = 52
export function ageDepth(weeks: number | null): number | null {
  if (weeks === null || weeks <= 0) return null
  const clamped = Math.min(AGE_CEILING, Math.max(AGE_FLOOR, weeks))
  return Math.log(clamped / AGE_FLOOR) / Math.log(AGE_CEILING / AGE_FLOOR)
}

/** The ramp's two ends, IN WORDS, derived from the constants above.
 *
 *  It exists because the legend used to say "4 yrs → 1 wk" as hardcoded prose sitting two
 *  inches from a swatch it was supposed to describe. That is two copies of one fact, and it
 *  is exactly how the lane column came to be captioned `oldest` after it stopped showing the
 *  oldest. Derived here, the label cannot drift from the ramp again. */
export function ageRampLabel(): string {
  const top = AGE_CEILING >= 104
    ? `${Math.round(AGE_CEILING / 52)} yrs`
    : AGE_CEILING >= 52
      ? '1 yr+'
      : `${AGE_CEILING} wks`
  return `${top} → ${AGE_FLOOR} wk`
}

/**
 * A lane cell. Behind = red, and since 20 Aug 2026 the DEPTH of that red is how late the
 * work being cleared is — same coverage as the hatch it replaces, with a reason.
 *
 * The hatch said "behind" thirteen times in a row and said nothing else; a group two weeks
 * late and a group four years late drew the identical wall. Depth answers the question the
 * hatch hid, and it is easier on the eye at thirteen cells across.
 *
 * A week that is behind but clears NOTHING — no capacity spent on the queue — keeps the
 * palest red rather than borrowing a neighbour's age: it is still behind, and nothing about
 * how late is being asserted.
 */
export function laneCell(w: WeekModel): { className?: string; background?: string } {
  if (w.carry > 0) return { background: ageRed(w.clearingAge) }
  if (w.free > w.capacity * 0.25) return { background: 'var(--lc-teal-soft)' }
  return { background: 'var(--lc-teal)' }
}

/** The accessible name for one column — the whole stack in words (§ Accessibility). */
export function columnLabel(w: WeekModel, levelled: boolean): string {
  if (!levelled) {
    return `${weekFull(w.weekStart)}: ${hrs(w.scheduled)} scheduled against ${hrs(w.capacity)} of capacity`
  }
  return (
    `${weekFull(w.weekStart)}: ${hrs(w.clearing)} clearing backlog, ${hrs(w.own)} due this week, ` +
    `${hrs(w.free)} free, ${hrs(w.carry)} still queued`
  )
}
